import { yupResolver } from '@hookform/resolvers/yup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ObjectSchema, Schema, boolean, date, object, string } from 'yup';
import type { MuiTelInputCountry } from 'mui-tel-input';

import { disableAutofillBg } from 'src/common/components/constants/disableAutofillBg';
import CountrySelect from 'src/common/components/form/CountrySelect';
import ControlledDatePicker from 'src/common/components/form/controlled/ControlledDatePicker';
import ControlledPhoneInput from 'src/common/components/form/controlled/ControlledPhoneInput';
import {
  ControlledRadioField,
  RadioOption,
} from 'src/common/components/form/controlled/ControlledRadioField';
import EditorFormActions from 'src/common/components/modals/EditorFormActions';
import { GENDERS } from 'src/common/constants/genders';
import { DEFAULT_PHONE_COUNTRY, phoneCountryToPreselect } from 'src/helpers/phoneCountry';
import { getParticipantAgeForEdition } from 'src/helpers/registrationPrice';
import { IFormParticipant, IParticipant } from 'src/interfaces/Participant';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface ParticipantEditorFormProps {
  handleClose?: () => void;
  handleSave: (editedParticipant: IFormParticipant, id?: string) => Promise<void> | void;
  isExternalSignup?: boolean;
  saveButtonText?: string;
  selectedParticipant?: IParticipant;
}

const ParticipantEditorForm = (props: ParticipantEditorFormProps) => {
  const {
    t,
  } = useTranslation();

  const {
    handleClose,
    handleSave,
    isExternalSignup = false,
    saveButtonText = t('form.save'),
    selectedParticipant,
  } = props;

  const { activeEditionId, editionsById } =
    useSystemOptionsStore(systemOptionsState => systemOptionsState);

  const participantSchema: ObjectSchema<IFormParticipant> = object()
    .shape({
      email: string().email().required(t('form.yup.genericRequired')),
      lastName: string().required(t('form.yup.genericRequired')),
      firstName: string().required(t('form.yup.genericRequired')),
      country: string().required(t('form.yup.genericRequired')),
      city: string().required(t('form.yup.genericRequired')),
      phoneNumber: string().required(t('form.yup.genericRequired')),
      gender: string().required(t('form.yup.genericRequired')),
      dateOfBirth: date()
        .optional()
        .nullable()
        .transform((value, originalValue) => {
          // Convert null to undefined to match TypeScript type
          return originalValue === null || originalValue === '' ? undefined : value;
        })
        .when([], {
          is: () => !selectedParticipant, // Required when creating new participant
          then: schema => schema.required(t('form.yup.genericRequired')),
          otherwise: schema => schema.notRequired(),
        }) as Schema<Date | undefined>,
      sportsGroupMember: boolean(),
      discounted: boolean(),
      sportsGroupName: string().when('sportsGroupMember', {
        is: (value: boolean | undefined) => !!value,
        then: schema => schema.required(t('form.yup.genericRequired')),
        otherwise: schema => schema.notRequired(),
      }),
      onlineSignup: boolean(),
      checkedIn: string(),
    })
    .required();

  const activeEdition = editionsById && activeEditionId ? editionsById[activeEditionId] : undefined;

  const toFormValues = (participant?: IParticipant): Partial<IFormParticipant> => {
    if (!participant) {
      return {
        gender: '',
        country: '',
        onlineSignup: isExternalSignup,
        dateOfBirth: undefined,
      };
    }
    const { age: _age, dateOfBirth, ...rest } = participant;
    return {
      ...rest,
      dateOfBirth: dateOfBirth
        ? (typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth)
        : undefined,
    };
  };

  const defaultValues = toFormValues(selectedParticipant);

  const {
    control,
    formState: { errors, isSubmitSuccessful },
    getValues,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
  } = useForm<IFormParticipant>({
    resolver: yupResolver(participantSchema) as Resolver<IFormParticipant>,
    defaultValues,
  });

  const [phoneDefaultCountry, setPhoneDefaultCountry] =
    useState<MuiTelInputCountry>(DEFAULT_PHONE_COUNTRY);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
      setPhoneDefaultCountry(DEFAULT_PHONE_COUNTRY);
      // Note: handleSuccessModalOpen is called explicitly from the save success handler
      // to ensure it only opens on actual success, not just form submission
    }
  }, [isSubmitSuccessful, reset]);

  // Repopulate form when selectedParticipant changes (e.g. opening edit for another participant)
  useEffect(() => {
    reset(toFormValues(selectedParticipant));
    setPhoneDefaultCountry(DEFAULT_PHONE_COUNTRY);
  }, [selectedParticipant, isExternalSignup, reset]);

  const onSubmit: SubmitHandler<IFormParticipant> = async editedParticipant => {
    const payload = { ...editedParticipant };
    delete (payload as { age?: number }).age;
    await handleSave(payload, selectedParticipant?.id);
    // isSubmitSuccessful is set by react-hook-form when this completes without throwing
  };

  const country = watch('country');
  const dateOfBirth = watch('dateOfBirth');

  const applyAddressCountryToPhone = (addressCountry: string | null) => {
    const nextPhoneCountry = phoneCountryToPreselect(addressCountry, getValues('phoneNumber'));
    if (!nextPhoneCountry) {
      return;
    }
    setPhoneDefaultCountry(nextPhoneCountry);
    setValue('phoneNumber', '');
  };

  // Age is display-only. Use the active edition date when one exists, otherwise today.
  const calculatedAge = useMemo(() => {
    if (!dateOfBirth) {
      return null;
    }
    const asOf = activeEdition?.date ?? new Date();
    const year = activeEdition?.year ?? asOf.getFullYear();
    return getParticipantAgeForEdition(
      { dateOfBirth, discounted: false },
      year,
      asOf,
    );
  }, [dateOfBirth, activeEdition]);

  const setSportsGroupMember = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('sportsGroupMember', event.target.checked);
  };

  const setOnlineSignup = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('onlineSignup', event.target.checked);
  };

  const setPensioner = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('discounted', event.target.checked);
  };

  const genderOptions: RadioOption[] = GENDERS.map(g => ({
    value: g.name,
    label: t(`base.gender.${g.name}`),
  }));

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('form.firstName')}
              variant="outlined"
              fullWidth
              margin="dense"
              error={!!errors['firstName']}
              helperText={errors['firstName']?.message as string | undefined}
              {...register('firstName')}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('form.lastName')}
              variant="outlined"
              fullWidth
              margin="dense"
              error={!!errors['lastName']}
              helperText={errors['lastName']?.message as string | undefined}
              {...register('lastName')}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('form.email')}
              variant="outlined"
              type="email"
              fullWidth
              margin="dense"
              error={!!errors['email']}
              helperText={errors['email']?.message as string | undefined}
              {...register('email')}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <CountrySelect
              textFieldProps={{
                label: t('form.country'),
                variant: 'outlined',
                margin: 'dense',
                error: !!errors['country'],
                helperText: errors['country']?.message as string | undefined,
              }}
              fullWidth
              preferredCountries={['RO', 'HU']}
              value={country || null}
              onChange={(_e, v) => {
                setValue('country', v || '');
                applyAddressCountryToPhone(v);
              }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('form.city')}
              variant="outlined"
              fullWidth
              margin="dense"
              error={!!errors['city']}
              helperText={errors['city']?.message as string | undefined}
              {...register('city')}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <ControlledPhoneInput
              control={control}
              name="phoneNumber"
              label={t('form.phoneNo')}
              fullWidth
              margin="dense"
              defaultCountry={phoneDefaultCountry}
              preferredCountries={['RO', 'HU']}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <ControlledRadioField<IFormParticipant>
              control={control}
              name="gender"
              margin="dense"
              fullWidth
              label={t('form.gender')}
              row
              options={genderOptions}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <ControlledDatePicker
              name="dateOfBirth"
              control={control}
              label={t('participants.dateOfBirth')}
              helperText={calculatedAge !== null ? t('participants.agePreview', { age: calculatedAge }) : undefined}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('form.age')}
              slotProps={{
                inputLabel: { shrink: calculatedAge != null },
                htmlInput: { readOnly: true },
              }}
              variant="outlined"
              fullWidth
              margin="dense"
              value={calculatedAge ?? ''}
              disabled
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControlLabel
              sx={{ display: 'block' }}
              control={
                <Switch
                  checked={watch('sportsGroupMember')}
                  onChange={setSportsGroupMember}
                  slotProps={{ input: { 'aria-label': t('form.sportsGroupMember') } }}
                />
              }
              label={t('form.sportsGroupMember')}
            />
            <FormControlLabel
              sx={{ display: 'block' }}
              control={
                <Switch
                  checked={watch('discounted')}
                  onChange={setPensioner}
                  slotProps={{ input: { 'aria-label': t('form.discounted') } }}
                />
              }
              label={t('form.discounted')}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              sx={disableAutofillBg}
              label={t('form.sportsGroupName')}
              variant="outlined"
              fullWidth
              margin="dense"
              disabled={!watch('sportsGroupMember')}
              error={!!errors['sportsGroupName']}
              helperText={errors['sportsGroupName']?.message as string | undefined}
              {...register('sportsGroupName')}
            />
          </Grid>
          {!isExternalSignup && (
            <Grid size={{ xs: 6 }}>
              <FormControlLabel
                sx={{ display: 'block' }}
                control={
                  <Switch
                    checked={watch('onlineSignup')}
                    onChange={setOnlineSignup}
                    slotProps={{ input: { 'aria-label': t('form.onlineSignup') } }}
                  />
                }
                label={t('form.onlineSignup')}
              />
            </Grid>
          )}

          {errors ? (
            <Grid size={{ xs: 12 }} sx={{ textAlign: 'right' }}>
              {Object.keys(errors).map(key => (
                <FormHelperText key={key} error>
                  {t('form.keysWithErrors', { key: t(`form.${key}`) })}
                </FormHelperText>
              ))}
            </Grid>
          ) : null}

          <EditorFormActions onCancel={handleClose} submitLabel={saveButtonText} />
        </Grid>
      </form>
    </>
  );
};

export default ParticipantEditorForm;

